import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import {
    Home,
    PenTool,
    Bell,
    Settings,
    User,
    Menu,
    X,
    LogOut,
    ShoppingCart,
    CalendarDays,
    UtensilsCrossed,
    ClipboardList,
    TrendingUp,
} from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { Feature } from '@/types/auth';

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const location = useLocation();
    const { user, logout, hasFeature } = useAuthStore();

    const allNavigation = [
        { name: 'Dashboard', href: '/dashboard', icon: Home, feature: null }, // Dashboard is always available
        // The progress tracker (#244). `feature: null` like Dashboard, My Menu
        // and Recipes: it is not gated behind a Feature enum value, so it needs
        // no migration and every client can reach it.
        //
        // Second, not last. Until now the only route into the tracker was two
        // cards near the bottom of the dashboard, below the fold — we built the
        // highest-value surface in the audit and then emailed people to nag them
        // about a page with no way in. /progress links on to /progress-report,
        // so one item covers both.
        { name: 'Progress', href: '/progress', icon: TrendingUp, feature: null },
        { name: 'My Menu', href: '/my-menu', icon: CalendarDays, feature: null }, // Program menu always available
        { name: 'Recipes', href: '/recipes', icon: UtensilsCrossed, feature: null }, // Recipes always available
        {
            name: 'Shopping',
            href: '/shopping-lists',
            icon: ShoppingCart,
            feature: Feature.SHOPPING_LISTS,
        },
        // Pantry is hidden from clients for now (#332). The /pantry route and the
        // backend stay in place; it is just not linked from anywhere.
        {
            name: 'Meals',
            href: '/meal-tracking',
            icon: ClipboardList,
            feature: Feature.MEAL_LOGGING,
        },
        { name: 'Journal', href: '/journal', icon: PenTool, feature: Feature.JOURNAL },
    ];

    // Filter navigation based on feature access
    const navigation = useMemo(() => {
        return allNavigation.filter((item) => {
            // Always show items without feature requirements
            if (!item.feature) return true;
            // Check if user has access to the feature
            return hasFeature(item.feature);
        });
    }, [hasFeature]);

    const isActive = (href: string) => location.pathname === href;

    const handleLogout = () => {
        logout();
        setProfileMenuOpen(false);
    };

    return (
        <header className="bg-brand-surface/95 backdrop-blur-sm shadow-sm border-b border-brand-surface-edge sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between gap-4 h-16">
                    {/* Logo and Main Navigation */}
                    <div className="flex min-w-0 items-center space-x-4 xl:space-x-8">
                        {/* Logo. The supplied asset is the full lockup — it
                            already contains the wordmark — so there is no text
                            span beside it; one would render the name twice.
                            width/height are the asset's own 88x80, so the box
                            is reserved before the image loads and the header
                            cannot shift. */}
                        <Link to="/dashboard" className="flex items-center shrink-0">
                            <img
                                src="/light2live_logo.png"
                                alt="Light2Live"
                                width={88}
                                height={80}
                                className="h-10 w-auto"
                            />
                        </Link>

                        {/* Desktop Navigation. With labels and full padding the
                            row needs about 1013px, so it overflowed and wrapped
                            from 768 to ~1045 (#301). It now steps down instead:
                            icons only from md (the name kept as aria-label
                            and as a tooltip), labels with tighter padding from
                            lg, and the original spacing from xl, so 1280 and
                            up look as before. */}
                        <nav className="hidden md:flex space-x-1">
                            {navigation.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        title={item.name}
                                        aria-label={item.name}
                                        className={`flex items-center whitespace-nowrap px-3 lg:px-2 xl:px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            // The wash is 1.16:1 against the
                                            // header, so it cannot carry the
                                            // active state on its own — the
                                            // full-strength border does, at
                                            // 7.29:1. Anything lighter fails
                                            // 1.4.11: even /60 measures 2.92.
                                            isActive(item.href)
                                                ? 'bg-brand-header-wash text-brand-text-blue border border-brand-header'
                                                : 'border border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4 shrink-0 lg:mr-1.5 xl:mr-2" />
                                        <span className="hidden lg:inline">{item.name}</span>
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Right Side - User Actions */}
                    <div className="flex items-center space-x-3">
                        {/* Notifications - placeholder for future messages/reminders */}
                        {/* text-gray-400 put the only glyph in this control at
                            2.6:1. The bell is the whole affordance here — there
                            is no label beside it — so it needs 1.4.11's 3:1. */}
                        <Button
                            variant="outline"
                            size="sm"
                            className="relative text-muted-foreground"
                        >
                            <Bell className="w-4 h-4" />
                        </Button>

                        {/* User Profile Dropdown */}
                        <div className="relative">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                                className="flex items-center space-x-2"
                            >
                                <div className="w-6 h-6 bg-brand-header rounded-full flex items-center justify-center">
                                    <span className="text-xs font-semibold text-brand-header-ink">
                                        {user?.firstName?.charAt(0) || 'U'}
                                    </span>
                                </div>
                                <span className="hidden sm:block text-sm">
                                    {user?.firstName || 'User'}
                                </span>
                            </Button>

                            {/* Dropdown Menu */}
                            {profileMenuOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setProfileMenuOpen(false)}
                                    ></div>
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                        <div className="px-4 py-2 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900">
                                                {user?.firstName} {user?.lastName}
                                            </p>
                                            <p className="text-xs text-gray-500">{user?.email}</p>
                                        </div>

                                        <Link
                                            to="/profile"
                                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                            onClick={() => setProfileMenuOpen(false)}
                                        >
                                            <User className="w-4 h-4 mr-3" />
                                            Profile
                                        </Link>

                                        <Link
                                            to="/settings"
                                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                            onClick={() => setProfileMenuOpen(false)}
                                        >
                                            <Settings className="w-4 h-4 mr-3" />
                                            Settings
                                        </Link>

                                        <hr className="my-1" />

                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <LogOut className="w-4 h-4 mr-3" />
                                            Sign out
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <Button
                            variant="outline"
                            size="sm"
                            className="md:hidden"
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileMenuOpen}
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? (
                                <X className="w-4 h-4" />
                            ) : (
                                <Menu className="w-4 h-4" />
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden border-t border-gray-200">
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        {navigation.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`flex items-center px-3 py-2 text-base font-medium rounded-lg ${
                                        isActive(item.href)
                                            ? 'bg-brand-header-wash text-brand-text-blue border-l-4 border-brand-header'
                                            : 'border-l-4 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <Icon className="w-5 h-5 mr-3" />
                                    {item.name}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
